import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Network, Plus, Upload, Loader2, Filter, X as XIcon, Trash2, ShieldOff,
  CheckCircle2, AlertTriangle, ExternalLink, Save as SaveIcon,
  Sparkles, Check, RotateCcw, Youtube, Eye, Globe, GraduationCap,
} from "lucide-react";
import {
  api,
  ENTITY_CATEGORY_NAMES,
  ENTITY_SOCIAL_PLATFORMS,
  type EntityView,
  type EntityDetailView,
  type EntityTypeView,
  type EntityCategoryNameView,
  type EntityColumnMappingView,
  type EntityImportPreviewResponse,
  type EntitySuggestionView,
  type EntitySuggestionStatusView,
} from "../../lib/api.js";
import { fileToCsv, SPREADSHEET_ACCEPT } from "../../lib/spreadsheet.js";

/**
 * Entities page — the Entity Intelligence Hub (Tier 0) operator surface.
 * Manual CRUD + CSV import + the do-not-mention content guardrail.
 * Mirrors the Content DB page conventions (KPI tiles, filter chips, table,
 * slide-out detail drawer).
 */

const ENTITY_TYPES: EntityTypeView[] = ["individual", "organization", "product", "platform", "publication"];

function prettify(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function EntitiesPage() {
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<EntityTypeView | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<EntityCategoryNameView | null>(null);
  const [excludeOnly, setExcludeOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);

  const overviewQuery = useQuery({
    queryKey: ["entities", "overview"],
    queryFn: () => api.getEntitiesOverview(),
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: ["entities", "list", { typeFilter, categoryFilter, excludeOnly, search }],
    queryFn: () =>
      api.listEntities({
        entityType: typeFilter ?? undefined,
        category: categoryFilter ?? undefined,
        excludeFromContent: excludeOnly ? true : undefined,
        q: search || undefined,
      }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["entities"] });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-brand-700" strokeWidth={2.2} />
            <h1 className="text-2xl font-semibold">Entities</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Competitors, peers, partners, influencers, and your existing therapist roster.
            Flag entities as <strong>do not mention</strong> to keep them out of produced content
            (allowed through on comparative / listicle pieces).
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button onClick={() => setShowDiscover(true)} className="btn-secondary btn-sm">
            <Sparkles className="h-4 w-4" /> Discover
          </button>
          <button onClick={() => setShowImport(true)} className="btn-secondary btn-sm">
            <Upload className="h-4 w-4" /> Import CSV
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-primary btn-sm">
            <Plus className="h-4 w-4" /> Add entity
          </button>
        </div>
      </header>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile label="Total entities" value={overviewQuery.data?.total} />
        <KpiTile label="Do not mention" value={overviewQuery.data?.excluded} tone="danger" />
        <KpiTile label="AlternaBe relationships" value={overviewQuery.data?.relationships} />
        <KpiTile label="Types" value={overviewQuery.data ? Object.keys(overviewQuery.data.byType).length : undefined} />
      </div>

      {/* Quick views — one-click category tabs (schools = education institutions). */}
      <div className="flex items-center gap-1 border-b border-line">
        <button
          onClick={() => { setCategoryFilter(null); setTypeFilter(null); setExcludeOnly(false); }}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${categoryFilter === null ? "border-brand-600 font-medium text-brand-800" : "border-transparent text-muted hover:text-ink"}`}>
          All entities
        </button>
        <button
          onClick={() => { setCategoryFilter("school"); setTypeFilter(null); setExcludeOnly(false); }}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${categoryFilter === "school" ? "border-brand-600 font-medium text-brand-800" : "border-transparent text-muted hover:text-ink"}`}>
          <GraduationCap className="h-4 w-4" /> מוסדות לימוד · Schools
          {overviewQuery.data?.byCategory?.school ? <span className="rounded-full bg-brand-50 px-1.5 text-2xs text-brand-800">{overviewQuery.data.byCategory.school}</span> : null}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 text-xs text-muted">
          <Filter className="h-3.5 w-3.5" /> Type:
        </div>
        <div className="flex flex-wrap gap-1">
          <Chip active={typeFilter === null} onClick={() => setTypeFilter(null)}>All</Chip>
          {ENTITY_TYPES.map((t) => (
            <Chip key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)}>{prettify(t)}</Chip>
          ))}
        </div>
        <select
          value={categoryFilter ?? ""}
          onChange={(e) => setCategoryFilter((e.target.value || null) as EntityCategoryNameView | null)}
          className="input max-w-[200px] py-1.5 text-xs"
        >
          <option value="">All categories</option>
          {ENTITY_CATEGORY_NAMES.map((c) => (
            <option key={c} value={c}>{prettify(c)}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" checked={excludeOnly} onChange={(e) => setExcludeOnly(e.target.checked)} />
          Do-not-mention only
        </label>
        <input
          type="text"
          placeholder="Search name, domain, alias…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input ml-auto max-w-xs py-1.5 text-sm"
        />
      </div>

      <EntityTable
        entities={listQuery.data?.items ?? []}
        loading={listQuery.isPending}
        onOpen={(id) => setSelectedId(id)}
      />

      {selectedId && (
        <EntityDetailDrawer id={selectedId} onClose={() => setSelectedId(null)} onChanged={invalidate} />
      )}
      {showAdd && (
        <AddEntityDialog
          onClose={() => setShowAdd(false)}
          onCreated={(id) => { setShowAdd(false); invalidate(); setSelectedId(id); }}
        />
      )}
      {showImport && (
        <ImportWizard onClose={() => setShowImport(false)} onDone={invalidate} />
      )}
      {showDiscover && (
        <DiscoverDrawer onClose={() => setShowDiscover(false)} onChanged={invalidate} onOpenEntity={(id) => { setShowDiscover(false); setSelectedId(id); }} />
      )}
    </div>
  );
}

// ── Discovery review queue (Tier 1) ──────────────────────────────────────────

function DiscoverDrawer({ onClose, onChanged, onOpenEntity }: { onClose: () => void; onChanged: () => void; onOpenEntity: (id: string) => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<EntitySuggestionStatusView>("pending");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, EntityCategoryNameView[]>>({});

  const suggestionsQuery = useQuery({
    queryKey: ["entities", "suggestions", tab],
    queryFn: () => api.listEntitySuggestions(tab),
  });
  const counts = suggestionsQuery.data?.counts;
  const items = suggestionsQuery.data?.items ?? [];

  const refetch = () => { qc.invalidateQueries({ queryKey: ["entities", "suggestions"] }); setSelected(new Set()); onChanged(); };

  const discover = useMutation({ mutationFn: () => api.discoverEntities({}), onSuccess: refetch });
  const regroup = useMutation({ mutationFn: () => api.regroupEntitySuggestions(true), onSuccess: refetch });
  const accept = useMutation({ mutationFn: (v: { id: string; categories?: EntityCategoryNameView[] }) => api.acceptEntitySuggestion(v.id, v.categories), onSuccess: refetch });
  const reject = useMutation({ mutationFn: (id: string) => api.rejectEntitySuggestion(id), onSuccess: refetch });
  const rename = useMutation({ mutationFn: (v: { id: string; name: string }) => api.renameEntitySuggestion(v.id, v.name), onSuccess: refetch });
  const reopen = useMutation({ mutationFn: (id: string) => api.reopenEntitySuggestion(id), onSuccess: refetch });
  const bulk = useMutation({ mutationFn: (v: { ids: string[]; action: "accept" | "reject" | "reopen" }) => api.bulkEntitySuggestions(v.ids, v.action), onSuccess: refetch });

  const toggleSelect = (id: string) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((s) => s.id)));
  // Include rename so Accept is disabled until an in-flight name edit persists —
  // accept() reads candidate_name from the DB, so this avoids a stale-name race.
  const busy = accept.isPending || reject.isPending || reopen.isPending || bulk.isPending || rename.isPending;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[1px] animate-fade-in" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-rail animate-slide-in-right">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold"><Sparkles className="h-4 w-4 text-brand-700" /> Discover entities</div>
            <div className="text-xs text-muted">Auto-find candidates from SERP competitors + scraped competitor pages.</div>
          </div>
          <button onClick={onClose} className="btn-icon"><XIcon className="h-4 w-4" /></button>
        </div>

        <div className="border-b border-line px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => discover.mutate()} disabled={discover.isPending} className="btn-primary btn-sm">
              {discover.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Run discovery
            </button>
            <button onClick={() => regroup.mutate()} disabled={regroup.isPending} className="btn-secondary btn-sm" title="Re-score which pending websites belong to an existing entity — no scraping, cheap. Use after adding entities by name.">
              {regroup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Regroup existing
            </button>
          </div>
          {discover.data && <p className="mt-2 text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" />{discover.data.summary}</p>}
          {discover.data && discover.data.warnings.length > 0 && (
            <p className="mt-1 text-2xs text-amber-700">{discover.data.warnings.join(" · ")}</p>
          )}
          {discover.isError && <p className="mt-2 text-xs text-danger">{(discover.error as Error).message}</p>}
          {regroup.data && <p className="mt-2 text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" />{regroup.data.summary}</p>}
          {regroup.data && regroup.data.warnings.length > 0 && (
            <p className="mt-1 text-2xs text-amber-700">{regroup.data.warnings.join(" · ")}</p>
          )}
          {regroup.isError && <p className="mt-2 text-xs text-danger">{(regroup.error as Error).message}</p>}
        </div>

        <div className="flex gap-1 border-b border-line px-3 py-2 text-sm">
          {(["pending", "accepted", "rejected"] as EntitySuggestionStatusView[]).map((t) => (
            <button key={t} onClick={() => { setTab(t); setSelected(new Set()); }} className={tab === t ? "rounded-lg bg-brand-50 px-3 py-1.5 font-medium text-brand-800" : "rounded-lg px-3 py-1.5 text-muted hover:bg-line/60"}>
              {prettify(t)}{counts ? ` (${counts[t]})` : ""}
            </button>
          ))}
        </div>

        {/* Bulk action bar */}
        {items.length > 0 && (
          <div className="flex items-center gap-3 border-b border-line bg-canvas px-4 py-2 text-xs">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all ({items.length})</label>
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-muted">{selected.size} selected:</span>
                {tab === "pending" && <button onClick={() => bulk.mutate({ ids: [...selected], action: "accept" })} disabled={busy} className="btn-primary btn-sm"><Check className="h-3.5 w-3.5" /> Accept</button>}
                {tab !== "rejected" && <button onClick={() => bulk.mutate({ ids: [...selected], action: "reject" })} disabled={busy} className="btn-secondary btn-sm">Reject</button>}
                {tab === "rejected" && <button onClick={() => bulk.mutate({ ids: [...selected], action: "reopen" })} disabled={busy} className="btn-secondary btn-sm">Reopen</button>}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
          {suggestionsQuery.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>}
          {suggestionsQuery.data && items.length === 0 && (
            <div className="card p-6 text-center text-sm text-muted">
              {tab === "pending" ? "No pending candidates. Run discovery to surface some (needs keywords with SERP data or scraped competitor pages)." : `No ${tab} candidates.`}
            </div>
          )}
          <div className="space-y-2">
            {items.map((s) => (
              <SuggestionCard
                key={s.id}
                s={s}
                selected={selected.has(s.id)}
                onToggleSelect={() => toggleSelect(s.id)}
                categories={overrides[s.id] ?? s.suggestedCategories}
                onChangeCategories={(cats) => setOverrides((p) => ({ ...p, [s.id]: cats }))}
                onAccept={() => accept.mutate({ id: s.id, categories: overrides[s.id] ?? s.suggestedCategories })}
                onRename={(name) => rename.mutate({ id: s.id, name })}
                onReject={() => reject.mutate(s.id)}
                onReopen={() => reopen.mutate(s.id)}
                onOpenEntity={onOpenEntity}
                busy={busy}
              />
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}

function SuggestionCard({ s, selected, onToggleSelect, categories, onChangeCategories, onAccept, onRename, onReject, onReopen, onOpenEntity, busy }: {
  s: EntitySuggestionView;
  selected: boolean; onToggleSelect: () => void;
  categories: EntityCategoryNameView[]; onChangeCategories: (c: EntityCategoryNameView[]) => void;
  onAccept: () => void; onRename: (name: string) => void; onReject: () => void; onReopen: () => void;
  onOpenEntity: (id: string) => void; busy: boolean;
}) {
  const removeCat = (c: EntityCategoryNameView) => onChangeCategories(categories.filter((x) => x !== c));
  const addCat = (c: EntityCategoryNameView) => { if (c && !categories.includes(c)) onChangeCategories([...categories, c]); };
  const [name, setName] = useState(s.candidateName);
  const commitName = () => { const t = name.trim(); if (t && t !== s.candidateName) onRename(t); else if (!t) setName(s.candidateName); };
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 gap-2">
          {s.status === "pending" && <input type="checkbox" checked={selected} onChange={onToggleSelect} className="mt-1" />}
          <div className="min-w-0">
            {s.status === "pending" ? (
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setName(s.candidateName); (e.target as HTMLInputElement).blur(); } }}
                disabled={busy}
                title="Edit the name before accepting — the entity is created with this"
                className="w-full rounded border border-transparent bg-transparent px-1 -mx-1 font-medium text-ink hover:border-line focus:border-brand-400 focus:bg-white focus:outline-none"
              />
            ) : (
              <div className="font-medium text-ink">{s.candidateName}</div>
            )}
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-2xs text-muted">
              {s.entityType && <span className="pill-muted">{prettify(s.entityType)}</span>}
              {s.classifierConfidence != null && <span>{Math.round(s.classifierConfidence * 100)}% conf</span>}
              <span className="pill-muted">{s.sourceType === "serp_competitor" ? "SERP" : s.sourceType === "competitor_extract" ? "extract" : "discovery"}</span>
              {s.linkToEntityId && (
                <span className="pill-info" title="Accepting will attach this to the existing entity, not create a new one">
                  → {s.linkToEntityName ? `belongs to ${s.linkToEntityName}` : "belongs to existing entity"}
                </span>
              )}
              {/* A NAME card that absorbed a website via Regroup — accepting yields one entity with both. */}
              {s.domain && s.domain.toLowerCase() !== s.candidateName.toLowerCase() && (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700" title="Grouped website — accepting creates one entity with this name + site">
                  <Globe className="h-3 w-3" /> {s.domain}
                </span>
              )}
            </div>
            {s.status === "rejected" && s.reviewedNotes && (
              <p className="mt-1 text-2xs text-muted italic">{s.reviewedNotes}</p>
            )}
            {s.classifierRationale && <p className="mt-1 text-xs text-muted">{s.classifierRationale}</p>}
            {/* Editable categories (pending) — the override passed to Accept */}
            {s.status === "pending" ? (
              <div className="mt-2 flex flex-wrap items-center gap-1">
                {categories.map((c) => (
                  <span key={c} className="pill-info">{prettify(c)}<button onClick={() => removeCat(c)} className="ml-1 hover:text-danger"><XIcon className="h-3 w-3" /></button></span>
                ))}
                <select value="" onChange={(e) => { if (e.target.value) addCat(e.target.value as EntityCategoryNameView); }} className="input py-0.5 text-2xs">
                  <option value="">+ category</option>
                  {ENTITY_CATEGORY_NAMES.filter((c) => !categories.includes(c)).map((c) => <option key={c} value={c}>{prettify(c)}</option>)}
                </select>
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1">{s.suggestedCategories.map((c) => <span key={c} className="pill-info">{prettify(c)}</span>)}</div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          {s.status === "pending" && (
            <>
              <button onClick={onAccept} disabled={busy} className="btn-primary btn-sm" title={s.linkToEntityId ? `Accept → attach to ${s.linkToEntityName ?? "existing entity"}` : "Accept → create entity"}><Check className="h-3.5 w-3.5" /></button>
              <button onClick={onReject} disabled={busy} className="btn-icon" title="Reject"><XIcon className="h-4 w-4" /></button>
            </>
          )}
          {s.status === "rejected" && (
            <button onClick={onReopen} disabled={busy} className="btn-ghost btn-sm text-xs" title="Reopen"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
          )}
          {s.status === "accepted" && s.acceptedEntityId && (
            <button onClick={() => onOpenEntity(s.acceptedEntityId!)} className="btn-ghost btn-sm text-xs">Open entity</button>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone }: { label: string; value: number | undefined; tone?: "danger" }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone === "danger" ? "text-danger" : "text-brand"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-brand px-3 py-1 text-xs font-medium text-white"
          : "rounded-full bg-line px-3 py-1 text-xs font-medium text-muted hover:bg-line/80 hover:text-ink"
      }
    >
      {children}
    </button>
  );
}

function EntityTable({ entities, loading, onOpen }: { entities: EntityView[]; loading: boolean; onOpen: (id: string) => void }) {
  if (loading) return <div className="card p-6 text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>;
  if (entities.length === 0) return <div className="card p-6 text-center text-sm text-muted">No entities yet. Add one, or import your therapist roster.</div>;
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-white">
      <table className="min-w-full divide-y divide-line text-sm">
        <thead className="bg-canvas text-2xs uppercase tracking-wider text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Domain</th>
            <th className="px-3 py-2 text-left">Contact</th>
            <th className="px-3 py-2 text-left">Flags</th>
            <th className="px-3 py-2 text-left">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {entities.map((e) => (
            <tr key={e.id} className="cursor-pointer hover:bg-canvas" onClick={() => onOpen(e.id)}>
              <td className="px-3 py-2.5 font-medium text-ink">
                {e.canonicalName}
                {e.displayNameHe && <span className="ml-2 text-xs text-muted">{e.displayNameHe}</span>}
              </td>
              <td className="px-3 py-2.5 text-muted">{prettify(e.entityType)}</td>
              <td className="px-3 py-2.5 text-xs text-muted">{e.domain ?? "—"}</td>
              <td className="px-3 py-2.5 text-xs" onClick={(ev) => ev.stopPropagation()}>
                {e.primaryEmail ? <a href={`mailto:${e.primaryEmail}`} className="text-brand-700 hover:underline">{e.primaryEmail}</a> : <span className="text-muted">—</span>}
                {e.primaryPhone && <div className="text-muted">{e.primaryPhone}</div>}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex flex-wrap gap-1">
                  {e.excludeFromContent && <span className="pill-danger">do not mention</span>}
                  {e.isExistingAlternabeRelationship && <span className="pill-info">{e.alternabeRelationshipKind ?? "relationship"}</span>}
                  {e.hasLivePage && <span className="pill-ok">live page</span>}
                </div>
              </td>
              <td className="px-3 py-2.5 text-xs text-muted">{new Date(e.updatedAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail drawer ──────────────────────────────────────────────────────────

type DetailTab = "overview" | "categories" | "social" | "contacts" | "relationships" | "seo" | "sources" | "history";

function EntityDetailDrawer({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<DetailTab>("overview");
  const detailQuery = useQuery({ queryKey: ["entities", "detail", id], queryFn: () => api.getEntity(id) });

  const refetch = () => { qc.invalidateQueries({ queryKey: ["entities", "detail", id] }); onChanged(); };
  const d = detailQuery.data;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[1px] animate-fade-in" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-rail animate-slide-in-right">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold">{d?.entity.canonicalName ?? "…"}</div>
            <div className="text-xs text-muted">{d ? prettify(d.entity.entityType) : ""}</div>
          </div>
          <button onClick={onClose} className="btn-icon" title="Close"><XIcon className="h-4 w-4" /></button>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-line px-3 py-2 text-sm">
          {(["overview", "categories", "social", "contacts", "relationships", "seo", "sources", "history"] as DetailTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={tab === t ? "rounded-lg bg-brand-50 px-3 py-1.5 font-medium text-brand-800" : "rounded-lg px-3 py-1.5 text-muted hover:bg-line/60"}
            >
              {prettify(t)}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
          {detailQuery.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>}
          {d && tab === "overview" && <OverviewTab d={d} onChanged={refetch} />}
          {d && tab === "categories" && <CategoriesTab d={d} onChanged={refetch} />}
          {d && tab === "social" && <SocialTab d={d} onChanged={refetch} />}
          {d && tab === "contacts" && <ContactsTab d={d} onChanged={refetch} />}
          {d && tab === "relationships" && <RelationshipsTab d={d} onChanged={refetch} />}
          {d && tab === "seo" && <SeoTab d={d} />}
          {d && tab === "sources" && <SourcesTab d={d} />}
          {d && tab === "history" && <HistoryTab d={d} />}
        </div>
      </aside>
    </>
  );
}

function OverviewTab({ d, onChanged }: { d: EntityDetailView; onChanged: () => void }) {
  const e = d.entity;
  const [canonicalName, setCanonicalName] = useState(e.canonicalName);
  const [displayNameHe, setDisplayHe] = useState(e.displayNameHe ?? "");
  const [displayNameEn, setDisplayEn] = useState(e.displayNameEn ?? "");
  const [primaryUrl, setPrimaryUrl] = useState(e.primaryUrl ?? "");
  const [excludeFromContent, setExclude] = useState(e.excludeFromContent);
  const [excludeReason, setReason] = useState(e.excludeReason ?? "");
  const [exceptions, setExceptions] = useState(e.excludeExceptions.join(", "));
  const [notes, setNotes] = useState(e.notes ?? "");

  const nameEmpty = canonicalName.trim().length === 0;
  const save = useMutation({
    mutationFn: () =>
      api.patchEntity(e.id, {
        canonicalName: canonicalName.trim(),
        displayNameHe: displayNameHe.trim() || null,
        displayNameEn: displayNameEn.trim() || null,
        primaryUrl: primaryUrl.trim() || null,
        excludeFromContent,
        excludeReason: excludeReason || null,
        excludeExceptions: exceptions.split(",").map((s) => s.trim()).filter(Boolean),
        notes: notes || null,
      }),
    onSuccess: onChanged,
  });
  const del = useMutation({ mutationFn: () => api.deleteEntity(e.id), onSuccess: onChanged });

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border border-line p-3">
        <div>
          <span className="label">Name</span>
          <input className="input" value={canonicalName} onChange={(ev) => setCanonicalName(ev.target.value)} placeholder="Canonical name" />
          {nameEmpty && <p className="mt-1 text-2xs text-danger">Name can't be empty.</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="label">Display name (HE)</span>
            <input className="input" dir="rtl" value={displayNameHe} onChange={(ev) => setDisplayHe(ev.target.value)} placeholder="—" />
          </div>
          <div>
            <span className="label">Display name (EN)</span>
            <input className="input" value={displayNameEn} onChange={(ev) => setDisplayEn(ev.target.value)} placeholder="—" />
          </div>
        </div>
        <div>
          <span className="label">Primary URL</span>
          <div className="flex items-center gap-2">
            <input className="input" value={primaryUrl} onChange={(ev) => setPrimaryUrl(ev.target.value)} placeholder="https://…" />
            {e.primaryUrl && (
              <a href={e.primaryUrl} target="_blank" rel="noreferrer" className="btn-icon shrink-0" title="Open">
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>
      {e.isExistingAlternabeRelationship && (
        <div className="rounded-lg border border-info/30 bg-info-soft px-3 py-2 text-xs text-blue-800">
          First-party AlternaBe relationship ({e.alternabeRelationshipKind ?? "member"}).
          {e.alternabeInternalId && <> Internal ID: <code>{e.alternabeInternalId}</code>.</>}
          {!e.hasLivePage && <> No live page — live-page / upsell gap.</>}
        </div>
      )}

      <div className="rounded-lg border border-line p-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={excludeFromContent} onChange={(ev) => setExclude(ev.target.checked)} />
          <ShieldOff className="h-4 w-4 text-danger" /> Exclude from produced content (do not mention)
        </label>
        {excludeFromContent && (
          <div className="mt-3 space-y-3">
            <div>
              <span className="label">Reason</span>
              <input className="input" value={excludeReason} onChange={(ev) => setReason(ev.target.value)} placeholder="e.g. direct competitor" />
            </div>
            <div>
              <span className="label">Allowed-through intents (comma list)</span>
              <input className="input" value={exceptions} onChange={(ev) => setExceptions(ev.target.value)} placeholder="comparative, listicle" />
              <p className="mt-1 text-2xs text-muted">The scrubber lets this entity through when the keyword intent is on this list.</p>
            </div>
          </div>
        )}
      </div>

      <div>
        <span className="label">Notes</span>
        <textarea className="textarea" value={notes} onChange={(ev) => setNotes(ev.target.value)} />
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => save.mutate()} disabled={save.isPending || nameEmpty} className="btn-primary btn-sm">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SaveIcon className="h-4 w-4" />} Save
        </button>
        <button
          onClick={() => { if (confirm(`Delete "${e.canonicalName}"? This removes all its records.`)) del.mutate(); }}
          disabled={del.isPending}
          className="btn-danger btn-sm"
        >
          <Trash2 className="h-4 w-4" /> Delete
        </button>
      </div>
      {save.isSuccess && <p className="text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" />Saved.</p>}
    </div>
  );
}

function CategoriesTab({ d, onChanged }: { d: EntityDetailView; onChanged: () => void }) {
  const [newCat, setNewCat] = useState<EntityCategoryNameView>("peer");
  const [newTag, setNewTag] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const addCat = useMutation({ mutationFn: () => api.addEntityCategory(d.entity.id, newCat), onSuccess: onChanged });
  const rmCat = useMutation({ mutationFn: (c: string) => api.removeEntityCategory(d.entity.id, c), onSuccess: onChanged });
  const addTag = useMutation({ mutationFn: () => api.addEntityTag(d.entity.id, newTag), onSuccess: () => { setNewTag(""); onChanged(); } });
  const rmTag = useMutation({ mutationFn: (t: string) => api.removeEntityTag(d.entity.id, t), onSuccess: onChanged });
  const addAlias = useMutation({ mutationFn: () => api.addEntityAlias(d.entity.id, newAlias), onSuccess: () => { setNewAlias(""); onChanged(); } });
  const rmAlias = useMutation({ mutationFn: (a: string) => api.removeEntityAlias(d.entity.id, a), onSuccess: onChanged });

  return (
    <div className="space-y-5">
      <section>
        <span className="label">Categories</span>
        <div className="mb-2 flex flex-wrap gap-1">
          {d.categories.length === 0 && <span className="text-xs text-muted">None.</span>}
          {d.categories.map((c) => (
            <span key={c.id} className="pill-muted">
              {prettify(c.category)}
              <button onClick={() => rmCat.mutate(c.category)} className="ml-1 text-muted hover:text-danger"><XIcon className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <select className="input py-1.5 text-xs" value={newCat} onChange={(e) => setNewCat(e.target.value as EntityCategoryNameView)}>
            {ENTITY_CATEGORY_NAMES.map((c) => <option key={c} value={c}>{prettify(c)}</option>)}
          </select>
          <button onClick={() => addCat.mutate()} className="btn-secondary btn-sm"><Plus className="h-3 w-3" /> Add</button>
        </div>
      </section>

      <section>
        <span className="label">Tags</span>
        <div className="mb-2 flex flex-wrap gap-1">
          {d.tags.length === 0 && <span className="text-xs text-muted">None.</span>}
          {d.tags.map((t) => (
            <span key={t.id} className="pill-muted">
              {t.tag}
              <button onClick={() => rmTag.mutate(t.tag)} className="ml-1 text-muted hover:text-danger"><XIcon className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="input py-1.5 text-xs" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="add a tag…" />
          <button onClick={() => newTag && addTag.mutate()} className="btn-secondary btn-sm"><Plus className="h-3 w-3" /> Add</button>
        </div>
      </section>

      <section>
        <span className="label">Aliases (feed the do-not-mention matcher)</span>
        <div className="mb-2 flex flex-wrap gap-1">
          {d.aliases.length === 0 && <span className="text-xs text-muted">None.</span>}
          {d.aliases.map((a) => (
            <span key={a.id} className="pill-muted">
              {a.aliasText}
              <button onClick={() => rmAlias.mutate(a.aliasText)} className="ml-1 text-muted hover:text-danger"><XIcon className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="input py-1.5 text-xs" value={newAlias} onChange={(e) => setNewAlias(e.target.value)} placeholder="add an alias / spelling…" />
          <button onClick={() => newAlias && addAlias.mutate()} className="btn-secondary btn-sm"><Plus className="h-3 w-3" /> Add</button>
        </div>
      </section>
    </div>
  );
}

function SocialTab({ d, onChanged }: { d: EntityDetailView; onChanged: () => void }) {
  const qc = useQueryClient();
  const [platform, setPlatform] = useState(ENTITY_SOCIAL_PLATFORMS[0]);
  const [handle, setHandle] = useState("");
  const [url, setUrl] = useState("");
  const [watchInput, setWatchInput] = useState("");
  const [watchMsg, setWatchMsg] = useState<string | null>(null);

  const watchedQuery = useQuery({ queryKey: ["entities", "watched", d.entity.id], queryFn: () => api.getEntityWatchedChannels(d.entity.id) });
  const invalidateWatched = () => qc.invalidateQueries({ queryKey: ["entities", "watched", d.entity.id] });

  const add = useMutation({
    mutationFn: () => api.addEntitySocialHandle(d.entity.id, { platform, handle, url: url || null }),
    onSuccess: () => { setHandle(""); setUrl(""); onChanged(); },
  });
  const togglePrimary = useMutation({ mutationFn: (h: { id: number; isPrimary: boolean }) => api.patchEntitySocialHandle(d.entity.id, h.id, { isPrimary: !h.isPrimary }), onSuccess: onChanged });
  const toggleVerified = useMutation({ mutationFn: (h: { id: number; verified: boolean }) => api.patchEntitySocialHandle(d.entity.id, h.id, { verified: !h.verified }), onSuccess: onChanged });
  const rm = useMutation({ mutationFn: (hid: number) => api.removeEntitySocialHandle(d.entity.id, hid), onSuccess: onChanged });
  const watch = useMutation({
    mutationFn: (input: string) => api.watchEntityYoutube(d.entity.id, { input }),
    onSuccess: (r) => {
      setWatchInput("");
      invalidateWatched();
      onChanged();
      const name = r.resolved.channelTitle ?? r.resolved.channelId;
      setWatchMsg(r.alreadyWatched ? `Already watching ${name}.` : `Watching ${name} — pulled ${r.poll?.itemsNew ?? 0} recent upload(s) into the discovery inbox.`);
    },
  });
  const unwatch = useMutation({ mutationFn: (feedId: string) => api.unwatchEntityChannel(d.entity.id, feedId), onSuccess: invalidateWatched });

  const watched = watchedQuery.data?.channels ?? [];

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        {d.socialHandles.length === 0 && <p className="text-xs text-muted">No handles yet.</p>}
        {d.socialHandles.map((h) => (
          <div key={h.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="font-medium">{prettify(h.platform)}</span> <span className="text-muted">{h.handle}</span>
              {h.isPrimary && <span className="ml-2 pill-info">primary</span>}
              {h.verified && <span className="ml-1 pill-ok">verified</span>}
            </div>
            <div className="flex shrink-0 gap-1">
              {h.platform === "youtube" && (
                <button onClick={() => watch.mutate(h.url || h.handle)} disabled={watch.isPending} className="btn-ghost btn-sm text-xs" title="Watch this channel for new uploads"><Eye className="h-3.5 w-3.5" /> Watch</button>
              )}
              <button onClick={() => togglePrimary.mutate({ id: h.id, isPrimary: h.isPrimary })} className="btn-ghost btn-sm text-xs">Primary</button>
              <button onClick={() => toggleVerified.mutate({ id: h.id, verified: h.verified })} className="btn-ghost btn-sm text-xs">Verify</button>
              <button onClick={() => rm.mutate(h.id)} className="btn-icon"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-lg border border-line p-3">
        <span className="label">Add a media channel</span>
        <div className="grid grid-cols-2 gap-2">
          <select className="input py-1.5 text-xs" value={platform} onChange={(e) => setPlatform(e.target.value as typeof platform)}>
            {ENTITY_SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{prettify(p)}</option>)}
          </select>
          <input className="input py-1.5 text-xs" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@handle" />
          <input className="input col-span-2 py-1.5 text-xs" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="full URL (optional)" />
        </div>
        <button onClick={() => handle && add.mutate()} disabled={add.isPending} className="btn-secondary btn-sm mt-2"><Plus className="h-3 w-3" /> Add handle</button>
      </section>

      {/* Watched YouTube channels */}
      <section>
        <div className="mb-2 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-muted">
          <Youtube className="h-3.5 w-3.5 text-red-600" /> Watched YouTube channels
        </div>
        <div className="space-y-2">
          {watched.length === 0 && <p className="text-xs text-muted">Not watching any channels for this entity. New uploads from a watched channel flow into the discovery inbox with scope-fit analysis.</p>}
          {watched.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium">{c.name}</span>
                <div className="text-2xs text-muted">
                  {c.lastFetchedAt ? `checked ${new Date(c.lastFetchedAt).toLocaleDateString()}` : "not checked yet"}
                  {!c.lastFetchSucceeded && <span className="ml-1 text-danger">· last poll failed</span>}
                  {!c.active && <span className="ml-1">· paused</span>}
                </div>
              </div>
              <button onClick={() => unwatch.mutate(c.id)} disabled={unwatch.isPending} className="btn-icon" title="Stop watching"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-2">
          <input className="input py-1.5 text-xs" value={watchInput} onChange={(e) => setWatchInput(e.target.value)} placeholder="@handle, channel URL, or UC… id" />
          <button onClick={() => watchInput.trim() && watch.mutate(watchInput.trim())} disabled={watch.isPending} className="btn-secondary btn-sm">
            {watch.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Watch
          </button>
        </div>
        {watchMsg && <p className="mt-1 text-2xs text-emerald-700">{watchMsg}</p>}
        {watch.isError && <p className="mt-1 text-2xs text-danger">{(watch.error as Error).message}</p>}
      </section>
    </div>
  );
}

function ContactsTab({ d, onChanged }: { d: EntityDetailView; onChanged: () => void }) {
  const [kind, setKind] = useState<"email" | "phone" | "whatsapp">("email");
  const [value, setValue] = useState("");
  const add = useMutation({ mutationFn: () => api.addEntityContact(d.entity.id, { kind, value }), onSuccess: () => { setValue(""); onChanged(); } });
  const rm = useMutation({ mutationFn: (cid: number) => api.removeEntityContact(d.entity.id, cid), onSuccess: onChanged });
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {d.contacts.length === 0 && <p className="text-xs text-muted">No contacts yet.</p>}
        {d.contacts.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
            <div><span className="font-medium">{prettify(c.kind)}</span> <span className="text-muted">{c.value}</span>{c.isPrimary && <span className="ml-2 pill-info">primary</span>}</div>
            <button onClick={() => rm.mutate(c.id)} className="btn-icon"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <select className="input py-1.5 text-xs" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option>
        </select>
        <input className="input py-1.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} placeholder="value" />
        <button onClick={() => value && add.mutate()} className="btn-secondary btn-sm"><Plus className="h-3 w-3" /> Add</button>
      </div>
    </div>
  );
}

function SourcesTab({ d }: { d: EntityDetailView }) {
  return (
    <div className="space-y-2 text-sm">
      {d.sources.length === 0 && <p className="text-xs text-muted">No provenance recorded.</p>}
      {d.sources.map((s) => (
        <div key={s.id} className="rounded-lg border border-line px-3 py-2">
          <div className="flex justify-between"><span className="font-medium">{prettify(s.sourceType)}</span><span className="text-2xs text-muted">{new Date(s.seenAt).toLocaleString()}</span></div>
          {s.sourceRef && <div className="text-xs text-muted">{s.sourceRef}</div>}
          {s.snippet && <div className="mt-1 text-xs text-muted">{s.snippet}</div>}
        </div>
      ))}
    </div>
  );
}

const REL_KINDS = ["affiliated_with", "practices_at", "authored_by", "subsidiary_of", "parent_company_of"] as const;

function RelationshipsTab({ d, onChanged }: { d: EntityDetailView; onChanged: () => void }) {
  const [kind, setKind] = useState<typeof REL_KINDS[number]>("affiliated_with");
  const [search, setSearch] = useState("");
  const [targetId, setTargetId] = useState("");

  const matches = useQuery({
    queryKey: ["entities", "list", "rel-picker", search],
    queryFn: () => api.listEntities({ q: search || undefined }),
    enabled: search.length >= 2,
  });
  const add = useMutation({
    mutationFn: () => api.addEntityRelationship(d.entity.id, targetId, kind),
    onSuccess: () => { setSearch(""); setTargetId(""); onChanged(); },
  });
  const rm = useMutation({ mutationFn: (rid: number) => api.removeEntityRelationship(d.entity.id, rid), onSuccess: onChanged });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {d.relationships.length === 0 && <p className="text-xs text-muted">No relationships yet.</p>}
        {d.relationships.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="text-muted">{r.parentName ?? r.parentEntityId}</span>
              <span className="mx-1 pill-muted">{prettify(r.kind)}</span>
              <span className="text-muted">{r.childName ?? r.childEntityId}</span>
            </div>
            <button onClick={() => rm.mutate(r.id)} className="btn-icon"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-line p-3 space-y-2">
        <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Add relationship (this entity → target)</div>
        <select className="input py-1.5 text-xs" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          {REL_KINDS.map((k) => <option key={k} value={k}>{prettify(k)}</option>)}
        </select>
        <input className="input py-1.5 text-xs" value={search} onChange={(e) => { setSearch(e.target.value); setTargetId(""); }} placeholder="search target entity by name…" />
        {search.length >= 2 && (
          <div className="max-h-40 overflow-y-auto rounded border border-line">
            {matches.data?.items.filter((e) => e.id !== d.entity.id).slice(0, 20).map((e) => (
              <button key={e.id} onClick={() => { setTargetId(e.id); setSearch(e.canonicalName); }} className={`block w-full px-2 py-1.5 text-left text-xs hover:bg-canvas ${targetId === e.id ? "bg-brand-50" : ""}`}>
                {e.canonicalName} <span className="text-muted">· {prettify(e.entityType)}</span>
              </button>
            ))}
          </div>
        )}
        <button onClick={() => targetId && add.mutate()} disabled={!targetId || add.isPending} className="btn-secondary btn-sm"><Plus className="h-3 w-3" /> Add</button>
      </div>
    </div>
  );
}

function SeoTab({ d }: { d: EntityDetailView }) {
  if (d.seoSignals.length === 0) {
    return <p className="text-xs text-muted">No SEO signals captured yet. These are populated in Tier 2 (per-entity SERP-visibility scoring across your cluster keywords).</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="min-w-full divide-y divide-line text-xs">
        <thead className="bg-canvas text-2xs uppercase text-muted"><tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">SERP appearances</th><th className="px-2 py-1.5 text-left">Organic kw</th><th className="px-2 py-1.5 text-left">Domain rating</th></tr></thead>
        <tbody className="divide-y divide-line">
          {d.seoSignals.map((s) => (
            <tr key={s.id}>
              <td className="px-2 py-1.5 text-muted">{new Date(s.fetchedAt).toLocaleDateString()}</td>
              <td className="px-2 py-1.5">{s.serpAppearances ?? "—"}</td>
              <td className="px-2 py-1.5">{s.organicKeywords ?? "—"}</td>
              <td className="px-2 py-1.5">{s.domainRating ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTab({ d }: { d: EntityDetailView }) {
  if (d.audit.length === 0) return <p className="text-xs text-muted">No changes recorded yet.</p>;
  return (
    <ol className="space-y-2 text-sm">
      {d.audit.map((a) => (
        <li key={a.id} className="flex gap-3 rounded-lg border border-line px-3 py-2">
          <span className="pill-muted shrink-0">{prettify(a.action)}</span>
          <div className="min-w-0">
            {a.summary && <div className="text-ink">{a.summary}</div>}
            <div className="text-2xs text-muted">{new Date(a.at + "Z").toLocaleString()}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Add entity dialog ────────────────────────────────────────────────────────

function AddEntityDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [canonicalName, setName] = useState("");
  const [entityType, setType] = useState<EntityTypeView>("organization");
  const [primaryUrl, setUrl] = useState("");
  const [excludeFromContent, setExclude] = useState(false);
  const create = useMutation({
    mutationFn: () => api.createEntity({ canonicalName, entityType, primaryUrl: primaryUrl || null, excludeFromContent }),
    onSuccess: (r) => onCreated(r.entity.id),
  });
  return (
    <Modal onClose={onClose} title="Add entity">
      <div className="space-y-3">
        <div><span className="label">Name</span><input className="input" value={canonicalName} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reflexology Center TLV" /></div>
        <div><span className="label">Type</span>
          <select className="input" value={entityType} onChange={(e) => setType(e.target.value as EntityTypeView)}>
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{prettify(t)}</option>)}
          </select>
        </div>
        <div><span className="label">Primary URL</span><input className="input" value={primaryUrl} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={excludeFromContent} onChange={(e) => setExclude(e.target.checked)} /><ShieldOff className="h-4 w-4 text-danger" /> Do not mention in content</label>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={() => canonicalName && create.mutate()} disabled={!canonicalName || create.isPending} className="btn-primary btn-sm">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── CSV import wizard ────────────────────────────────────────────────────────

function ImportWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<"upload" | "map" | "done">("upload");
  const [csvContent, setCsv] = useState("");
  const [preview, setPreview] = useState<EntityImportPreviewResponse | null>(null);
  const [mappings, setMappings] = useState<EntityColumnMappingView[]>([]);
  const [fileErr, setFileErr] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: (text: string) => api.entityImportPreview(text),
    onSuccess: (r) => { setPreview(r); setMappings(r.mappings); setStep("map"); },
  });
  const commitMut = useMutation({
    mutationFn: () => api.entityImportCommit(csvContent, mappings),
    onSuccess: () => { setStep("done"); onDone(); },
  });

  async function handleFile(file: File) {
    setFileErr(null);
    try {
      const text = await fileToCsv(file);
      setCsv(text);
      previewMut.mutate(text);
    } catch (e) {
      setFileErr(`Couldn't read "${file.name}": ${(e as Error).message}`);
    }
  }

  return (
    <Modal onClose={onClose} title="Import therapist roster (CSV or Excel)" wide>
      {step === "upload" && (
        <div className="space-y-3">
          <p className="text-sm text-muted">Upload your membership roster. Each row imports as an <strong>individual</strong> entity with an existing AlternaBe relationship. Claude Haiku auto-detects the columns; you confirm the mapping next.</p>
          <input type="file" accept={SPREADSHEET_ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} className="input" />
          {previewMut.isPending && <p className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Parsing + detecting columns…</p>}
          {fileErr && <p className="text-xs text-danger">{fileErr}</p>}
          {previewMut.isError && <p className="text-xs text-danger">{(previewMut.error as Error).message}</p>}
        </div>
      )}

      {step === "map" && preview && (
        <div className="space-y-3">
          <p className="text-sm text-muted">{preview.preview.totalRows} rows. Confirm or override the column mapping (model: {preview.mapperModel}).</p>
          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-line">
            <table className="min-w-full divide-y divide-line text-xs">
              <thead className="bg-canvas text-2xs uppercase text-muted"><tr><th className="px-2 py-1.5 text-left">CSV column</th><th className="px-2 py-1.5 text-left">Maps to</th><th className="px-2 py-1.5 text-left">Sample</th></tr></thead>
              <tbody className="divide-y divide-line">
                {mappings.map((m, i) => (
                  <tr key={m.source}>
                    <td className="px-2 py-1.5 font-medium">{m.source}</td>
                    <td className="px-2 py-1.5">
                      <select
                        className="input py-1 text-xs"
                        value={m.target}
                        onChange={(e) => setMappings((prev) => prev.map((x, j) => j === i ? { ...x, target: e.target.value } : x))}
                      >
                        {["internal_id","first_name","last_name","display_name","email","phone","whatsapp","subscription_tier","subscription_status","start_date","end_date","profile_status","is_active","is_verified","verification_status","alternabe_url","location","ignore"].map((t) => (
                          <option key={t} value={t}>{prettify(t)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-muted">{preview.preview.rows[0]?.[m.source] ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {commitMut.isError && <p className="text-xs text-danger">{(commitMut.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setStep("upload")} className="btn-secondary btn-sm">Back</button>
            <button onClick={() => commitMut.mutate()} disabled={commitMut.isPending} className="btn-primary btn-sm">
              {commitMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import {preview.preview.totalRows} rows
            </button>
          </div>
        </div>
      )}

      {step === "done" && commitMut.data && (
        <div className="space-y-3 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <div className="text-lg font-semibold">Import complete</div>
          <p className="text-sm text-muted">
            {commitMut.data.created} created · {commitMut.data.skipped} skipped · {commitMut.data.contactsAdded} contacts · {commitMut.data.tagsAdded} tags
          </p>
          {commitMut.data.warnings.length > 0 && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-left text-xs text-amber-800">
              <div className="mb-1 flex items-center gap-1 font-medium"><AlertTriangle className="h-3 w-3" /> {commitMut.data.warnings.length} warning(s)</div>
              <ul className="list-disc pl-4">{commitMut.data.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <button onClick={onClose} className="btn-primary btn-sm">Done</button>
        </div>
      )}
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className={`card w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-5 scrollbar-thin`}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button onClick={onClose} className="btn-icon"><XIcon className="h-4 w-4" /></button>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}
